import { useEffect, useState, type ReactNode } from "react";
import { Outlet } from "react-router";
import { IntlProvider, useLocale, useMessages, useTimeZone, type AbstractIntlMessages } from "use-intl";
import { LoadingState } from "@/components/ui/loading-state";
import { loadAdminMessages } from "@/i18n/load-messages";
import type { AppLocale } from "@/i18n/routing";
import { AdminSidebar } from "@/features/admin-cms/components/admin-sidebar";
import { AdminHeader } from "@/features/admin-cms/components/admin-header";
import { RequireAdmin } from "@/features/auth/components/route-guards";
import { sessionDisplayName, useSession } from "@/features/auth/hooks/use-session";

/**
 * The admin CMS shell. RequireAdmin renders nothing of it for anyone the
 * backend's policy wouldn't treat as admin (signed out → /login, signed-in
 * non-admin → home); every /api/v1/admin route enforces the same rule
 * server-side, which is the actual security boundary.
 */
export function AdminLayout() {
  return (
    <RequireAdmin>
      <WithAdminMessages>
        <AdminChrome />
      </WithAdminMessages>
    </RequireAdmin>
  );
}

/**
 * The page-wide catalog leaves out the admin namespace (students never need it — see load-messages.ts);
 * this loads it once an admin is confirmed and re-provides the catalog with it merged in.
 */
function WithAdminMessages({ children }: { children: ReactNode }) {
  const locale = useLocale() as AppLocale;
  const messages = useMessages();
  const timeZone = useTimeZone();
  const [admin, setAdmin] = useState<{ locale: string; messages: AbstractIntlMessages } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadAdminMessages(locale).then((loaded) => {
      if (!cancelled) setAdmin({ locale, messages: loaded });
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  if (!admin || admin.locale !== locale) return <LoadingState fullPage message="" />;
  return (
    <IntlProvider locale={locale} timeZone={timeZone} messages={{ ...messages, ...admin.messages }}>
      {children}
    </IntlProvider>
  );
}

function AdminChrome() {
  const { data: user } = useSession();
  if (!user) return null;

  return (
    <div className="flex min-h-full flex-1">
      <div className="contents print:hidden">
        <AdminSidebar />
      </div>
      {/*
        min-w-0 here is load-bearing, not decorative: this column is a
        `flex-1` item inside the row above (sidebar + content), and flex
        items default to `min-width: auto` — meaning a flex-1 item still
        refuses to shrink below its CONTENT's natural width. Any long
        unwrapped string anywhere inside the page (e.g. a question
        prompt in a `truncate` list — see admin-dashboard-page.tsx's recent-activity
        list) was stretching this whole column, and therefore <main> and
        everything inside it, out to that string's full natural width
        instead of being capped at "viewport minus sidebar" — which is
        what actually caused the admin dashboard's charts/lists to run off
        the right edge with a page-wide horizontal scrollbar. min-w-0
        overrides that default so this column (and everything nested in
        it) is properly capped to the remaining space, and each
        `truncate`'s own min-w-0 can then actually do its job.
      */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="contents print:hidden">
          <AdminHeader displayName={sessionDisplayName(user)} email={user.email} />
        </div>
        <main className="flex-1 bg-muted/30 p-4 sm:p-6 lg:p-8 print:bg-transparent print:p-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
